<template>
  <div class="w-full h-full pt-5">
    <div class="flex items-center px-4">
      <back-button
        label="Back"
        variant="secondary"
        @onClick="$router.back()"
        class="mr-3"
      />
      <div class="divider-line mr-3"></div>
      <h1 class="text-xl text-left font-extrabold text-darkPurple mr-4">
        Job Cadres
      </h1>
      <Breadcrumb :items="breadcrumbs" />
    </div>

    <div class="mx-3 mt-5">
      <Card class="p-4" :footer-has-border="false" style="border-radius: 16px;">

        <div>
            <h2 class="text-lg font-bold text-darkPurple">Job Cadres</h2>
            <div class="flex items-center mt-1">
              <p class="text-sm font-semibold text-jet">
                Enable if this organisation operates as an educational institution
              </p>
              <label
                class="switch ml-2"
                :class="{ 'switch--disabled': educationalInstitutionToggleDisabled }"
              >
                <input
                  type="checkbox"
                  :checked="educationalInstitution"
                  :disabled="educationalInstitutionToggleDisabled"
                  @click.prevent="onEducationalInstitutionToggle"
                />
                <span class="slider"></span>
              </label>
            </div>
        </div>

        <div class="cadre-footer mt-4">
            <div class="pt-4">
              <Tabs
                v-if="educationalInstitution"
                class="mt-6 mx-4"
                :tabs="tabsItems"
                :active-tab="activeTab"
                @currentTab="onCadreAcademicTabChange($event)"
                :border="true"
              />
            </div>
            <div class="flex items-center pt-4 justify-end gap-4">
                <Button
                    :background="appearance.buttonColor || defaultBTNColor"
                    class="text-white"
                    @click="openDrawer"
                >
                    Add New
                </Button>
                <Button
                    background-color="none"
                    class="text-dynamicBackBtn border border-solid border-dynamicBackBtn"
                    @click="handleImport"
                >
                    Import
                </Button>
            </div>
        </div>

        <CardFooter
          class="mt-4 cadre-footer"
          :show-sort-filter="true"
          :show-search="true"
          :showfilter="true"
          showcalendar
          search-placeholder="Search"
          reloadcard
          @searchResult="handleSearch"
          @sortType="handleSort"
          @calendar="handleCalendar"
          @reload="handleReload"
        />
      </Card>
    </div>

    <div v-if="!showError" class="w-full h-full p-3">
      <NoOrgRegister
        v-if="!loadingData && !cadreRows.length"
        title="No job cadres have been created yet."
        subtitle="Once a job cadre is created, it will automatically appear in this register."
      />
      <sTable
        v-else
        :headers="cadreHeaders"
        :items="cadreRows"
        :pagination-list="metaData"
        :loading="loadingData"
        aria-label="job cadre table"
        class="w-full"
        @page="handlePage($event)"
        @itemsPerPage="handleItemsPerPage($event)"
        has-number
        page-sync
      >
        <template v-slot:item="{ item }">
          <span v-if="item.id">
            <Menu left top="-150" margin="24" class="my-2 p-0">
              <template v-slot:title>
                <icon icon-name="more_icon" size="xs" />
              </template>
              <div style="width: 116px; height: 81px" class="py-3 px-2">
                <div
                  class="flex py-1 px-3 more h-8 cursor-pointer"
                  @click="handleEditJobCadre(item.data)"
                >
                  <icon
                    icon-name="edit"
                    class-name="text-blueCrayola mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Edit</p>
                </div>
                <div
                  class="flex px-3 py-1 more h-8 cursor-pointer"
                  @click="openDeleteJobCadreDialog(item.data)"
                >
                  <icon
                    icon-name="delete"
                    class-name="text-desire mr-2"
                    size="xsm"
                  />
                  <p class="pt-1">Delete</p>
                </div>
              </div>
            </Menu>
          </span>
        </template>
      </sTable>
    </div>
    <div v-else>
      <error-page />
    </div>

    <CadreDrawer
      :is-open="showCadreDrawer"
      :is-edit="Boolean(selectedJobCadre && selectedJobCadre.id)"
      :initial-data="selectedJobCadre"
      @close="closeDrawer"
      @save="handleSaveCadre"
    />
    <import-employee :open-modal="isOpenImport" @close="isOpenImport = false"
      :isOverwriteEmployee="false" :isTemplate="true" @download="downloadJobCadreTemplate()"
      @reload-employees="handleReload()" @importEmployee="importJobCadre" :url=" url"
      :title="`Import Job Cadre Data`" :is-employee-directory="false" :isDisplayProgressBar="false"
    />

    <Modal v-if="dialogEducationalInstitution">
      <Card class="p-5 flex flex-col max-w-md">
        <div>
          <p class="font-bold ml-4 my-5 text-center">
            This is a one-time action for your organisation. It cannot be undone after you confirm. Do you want to continue?
          </p>
          <div class="flex">
            <Button
              class="text-white"
              :background="appearance.buttonColor || defaultBTNColor"
              :disabled="confirmingEducationToggle"
              @click="confirmEducationalInstitutionToggle"
            >
              Yes
            </Button>
            <Button
              background-color="none"
              class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
              @click="closeEducationalInstitutionDialog"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>

    <Modal v-if="dialogDeleteJobCadre">
      <Card class="p-5 flex flex-col max-w-md">
        <div>
          <p class="font-bold ml-4 my-5 text-center">
            <template v-if="pendingDeleteJobCadreName">
              Are you sure you want to delete "{{ pendingDeleteJobCadreName }}"?
            </template>
            <template v-else>
              Are you sure you want to delete this job cadre?
            </template>
          </p>
          <div class="flex">
            <Button
              class="text-white"
              :background="appearance.buttonColor || defaultBTNColor"
              :disabled="deletingJobCadre"
              @click="confirmDeleteJobCadre"
            >
              Yes
            </Button>
            <Button
              background-color="none"
              class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
              @click="closeDeleteJobCadreDialog"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Tabs from "@scelloo/cloudenly-ui/src/components/tab";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Button from "@/components/Button";
import Card from "@/components/Card";
import CardFooter from "@/components/CardFooter";
import Icon from "@/components/Icon";
import Menu from "@/components/Menu";
import ImportEmployee from "@/components/importEmployee";
import Modal from "@/components/Modal";
import ErrorPage from "@/modules/Admin/error403";
import NoOrgRegister from "@/modules/Admin/OrganisationModule/NoOrgRegister.vue";
import CadreDrawer from "./CadreDrawer";
import { JOB_CLASSIFICATION_TEMPLATE_PATH } from "../constants";

export default {
  name: "Cadre",
  components: {
    BackButton,
    Breadcrumb,
    Tabs,
    STable,
    Button,
    Card,
    CardFooter,
    Icon,
    Menu,
    ImportEmployee,
    Modal,
    NoOrgRegister,
    CadreDrawer,
    ErrorPage,
  },
  data() {
    return {
      showError: false,
      dialogEducationalInstitution: false,
      pendingEducationalInstitution: false,
      hasExistingCadre: false,
      confirmingEducationToggle: false,
      dialogDeleteJobCadre: false,
      pendingDeleteJobCadreId: null,
      pendingDeleteJobCadreName: "",
      deletingJobCadre: false,
      url: `${process.env.VUE_APP_UPLOAD_CLOUDENLY_FILES}/document-management/upload/job-classifications`,
      isOpenImport: false,
      showCadreDrawer: false,
      selectedJobCadre: null,
      loadingData: true,
      metaData: {
        total: 0,
        page: 1,
        lastPage: 1,
        nextPage: null,
        prevPage: null,
        from: 0,
        to: 0,
      },
      searchWord: null,
      sortType: null,
      numberOfPage: 1,
      itemsPerPage: 20,
      educationalInstitution: false,
      activeTab: "Academic",
      tabsItems: ["Academic", "Non-Academic"],
      breadcrumbs: [
        {
          disabled: false,
          text: "Organisation Design",
          id: "",
        },
        {
          disabled: false,
          text: "Job Cadres",
          id: "Job Cadres",
        },
      ],
      cadreRows: [],
    };
  },
  computed: {
    cadreHeaders() {
      return [
        { title: "JOB CADRES", value: "cadre" },
        { title: "DESCRIPTION", value: "description" },
        { title: "POST COUNT", value: "postCount" },
        { title: "EMPLOYEES COUNT", value: "employeesCount" },
        { title: "", value: "id", image: true },
      ];
    },
    isAcademicJobCadre() {
      if (!this.educationalInstitution) {
        return null;
      }

      return this.activeTab === "Academic";
    },
    educationalInstitutionToggleDisabled() {
      return this.hasExistingCadre;
    },
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),
  },
  methods: {
    async onEducationalInstitutionToggle() {
      if (this.educationalInstitutionToggleDisabled) {
        return;
      }
      try {
        await this.$handlePrivilege("jobCadre", "enableEducationalCategory");
        this.pendingEducationalInstitution = !this.educationalInstitution;
        this.dialogEducationalInstitution = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async confirmEducationalInstitutionToggle() {
      if (this.confirmingEducationToggle) {
        return;
      }
      this.confirmingEducationToggle = true;
      try {
        await this.$_toggleEducation({
          isEducationalInstitution: this.pendingEducationalInstitution,
        });
        this.educationalInstitution = this.pendingEducationalInstitution;
        this.dialogEducationalInstitution = false;
        this.queryUpdate();
        this.$toasted.success("Educational institution setting updated", {
          duration: 5000,
        });
      } catch (error) {
        this.$toasted.error("Unable to update educational institution setting", { duration: 5000 });
      } finally {
        this.confirmingEducationToggle = false;
      }
    },
    closeEducationalInstitutionDialog() {
      this.dialogEducationalInstitution = false;
    },
    onCadreAcademicTabChange(tab) {
      this.activeTab = tab;
      this.numberOfPage = 1;
      this.queryUpdate();
    },
    queryUpdate() {
      const search = this.searchWord ? `&search=${this.searchWord}` : "";
      const sort = this.sortType ? `&sort=${this.sortType}` : "";
      const pageNumber = this.numberOfPage ? `&page=${this.numberOfPage}` : "";
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      const isAcademic =
        this.isAcademicJobCadre === null ? "" : `&isAcademic=${this.isAcademicJobCadre}`;
      this.getJobClassifications(
        `${sort}${search}${pageNumber}${itemPage}${isAcademic}`
      );
      this.refreshCadreExistence();
    },
    async refreshCadreExistence() {
      try {
        const response = await this.$_getJobClassification("&perPage=100");
        const classifications = response?.data?.jobClassifications || [];
        this.hasExistingCadre = classifications.some(
          (item) => item.type === "jobCadre"
        );
      } catch (error) {
        this.hasExistingCadre = false;
      }
    },
    async getJobClassifications(payload = "") {
      this.loadingData = true;
      try {
        const response = await this.$_getJobClassification(payload);
        const classifications = response?.data?.jobClassifications || [];
        this.cadreRows = classifications
          .filter((item) => item.type === "jobCadre")
          .map((item) => ({
            id: item.id,
            cadre: item.name,
            description: item.description || "------",
            postCount: item.numberOfDesignations ?? 0,
            employeesCount: item.numberOfEmployees ?? 0,
          }));
        this.metaData = response?.data?.meta || {
          total: this.cadreRows.length,
          page: 1,
          lastPage: 1,
          nextPage: null,
          prevPage: null,
          from: this.cadreRows.length ? 1 : 0,
          to: this.cadreRows.length,
        };
      } catch (error) {
        this.cadreRows = [];
        this.metaData = {
          total: 0,
          page: 1,
          lastPage: 1,
          nextPage: null,
          prevPage: null,
          from: 0,
          to: 0,
        };
      } finally {
        this.loadingData = false;
      }
    },
    async importJobCadre(fileUrl, overwriteEmployee, { resolve, reject }) {
      try {
        const payload = {
          creator: this.$AuthUser.id,
          fileUrl,
        };
        const response = await this.$_importJobClassification(payload);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    },
    async downloadJobCadreTemplate() {
      try {
        const link = document.createElement("a");
        link.href = JOB_CLASSIFICATION_TEMPLATE_PATH;
        link.download = "Job Cadre Template";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        return this.$toasted.error("Error downloading file", { duration: 6000 });
      }
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.queryUpdate();
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },
    async openDrawer() {
      try {
        await this.$handlePrivilege("jobCadre", "addNewJobCadre");
        this.selectedJobCadre = null;
        this.showCadreDrawer = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleImport() {
      try {
        await this.$handlePrivilege("jobCadre", "importJobCadre");
        this.isOpenImport = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeDrawer() {
      this.showCadreDrawer = false;
      this.selectedJobCadre = null;
    },
    async handleEditJobCadre(jobCadre) {
      try {
        await this.$handlePrivilege("jobCadre", "editJobCadre");
        this.selectedJobCadre = { ...jobCadre };
        this.showCadreDrawer = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    async handleSaveCadre(payload) {
      if (payload.isEdit && payload.id) {
        await this.updateJobCadre(payload);
        return;
      }

      await this.createJobCadre(payload);
    },
    async updateJobCadre(payload) {
      try {
        await this.$_editJobClassification(payload.id, {
          name: payload.cadre,
          description: payload.description || "",
          type: "jobCadre",
          isAcademic: this.isAcademicJobCadre,
        });
        this.$toasted.success("Job cadre updated successfully", {
          duration: 5000,
        });
        this.queryUpdate();
        this.closeDrawer();
      } catch (error) {
        this.$toasted.error("Unable to update job cadre", {
          duration: 5000,
        });
      }
    },
    async createJobCadre(payload) {
      try {
        await this.$_createJobClassification({
          name: payload.cadre,
          description: payload.description || "",
          type: "jobCadre",
          isAcademic: this.isAcademicJobCadre,
        });
        this.$toasted.success("Job cadre created successfully", {
          duration: 5000,
        });
        this.queryUpdate();
        this.closeDrawer();
      } catch (error) {
        this.$toasted.error("Unable to create job cadre", {
          duration: 5000,
        });
      }
    },
    async openDeleteJobCadreDialog(row) {
      if (!row || !row.id) {
        return;
      }
      try {
        await this.$handlePrivilege("jobCadre", "deleteJobCadre");
        this.pendingDeleteJobCadreId = row.id;
        this.pendingDeleteJobCadreName = row.cadre || "";
        this.dialogDeleteJobCadre = true;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    closeDeleteJobCadreDialog() {
      this.dialogDeleteJobCadre = false;
      this.pendingDeleteJobCadreId = null;
      this.pendingDeleteJobCadreName = "";
    },
    async confirmDeleteJobCadre() {
      if (!this.pendingDeleteJobCadreId || this.deletingJobCadre) {
        return;
      }
      this.deletingJobCadre = true;
      try {
        await this.$_deleteJobClassification(this.pendingDeleteJobCadreId);
        this.$toasted.success("Job cadre deleted successfully", {
          duration: 5000,
        });
        this.closeDeleteJobCadreDialog();
        this.queryUpdate();
      } catch (error) {
        this.$toasted.error("Unable to delete job cadre", {
          duration: 5000,
        });
      } finally {
        this.deletingJobCadre = false;
      }
    },
    handleSearch(searchWord) {
      this.loadingData = true;
      if (searchWord) {
        this.searchWord = searchWord;
        this.queryUpdate();
      } else {
        this.searchWord = null;
        this.queryUpdate();
      }
    },
    handleSort(sortType) {
      this.loadingData = true;
      const changeSortType = sortType.split(":");
      let result;
      if (changeSortType[0] === "createdAt") {
        result = sortType;
      } else {
        result = `name:${changeSortType[1]}`;
      }
      this.sortType = result;
      this.queryUpdate();
    },
    handleCalendar() {},
    handleReload() {
      this.queryUpdate();
    },
  },
  
  async created() {
    try {
      const { data } = await this.$_getOrgProfile();
      this.educationalInstitution = Boolean(data?.org.isEducationalInstitution);
    } catch (error) {
      this.educationalInstitution = false;
    }
    await this.refreshCadreExistence();
  },
  async mounted() {
    try {
      await this.$handlePrivilege("jobCadre", "viewRegister");
      this.showError = false;
      this.queryUpdate();
    } catch (error) {
      this.showError = true;
      this.loadingData = false;
    }
  },
};
</script>

<style scoped>
.divider-line {
  border-left: 1px solid #878e99;
  height: 1.5rem;
}

.cadre-footer {
  border-top: 1px solid #0000001a;
}

.more:hover {
  background: #eeeeee;
}

.switch {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EEEEEE;
  transition: 0.2s;
  border-radius: 9999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 2px;
  bottom: 2px;
  background-color: #878E99;
  transition: 0.2s;
  border-radius: 9999px;
}

input:checked + .slider:before {
  transform: translateX(16px);
  background-color: #13B56A;
}

.switch--disabled {
  pointer-events: none;
  opacity: 0.65;
}

.switch--disabled .slider {
  cursor: not-allowed;
}

</style>